/**
 * Build a CMS image URL against a Sanity image-transformation proxy.
 *
 * The asset reference is the Sanity asset filename:
 *   `<40-char-hex>-<width>x<height>.<ext>`
 *
 * Sanity's image-URL API is documented at
 * https://www.sanity.io/docs/image-urls — this helper exposes the
 * transformation params we actually use (size, format, quality, crop, etc.)
 * and serializes them as query params in a deterministic order.
 *
 * Safety:
 *   - baseUrl must be a valid http(s) URL.
 *   - assetRef must match the Sanity `<hash>-WxH.ext` pattern.
 *   - Numeric options are range-checked; out-of-range values throw.
 *
 * Invalid input throws synchronously so typos surface at module-init time
 * rather than producing a broken `undefined/...` URL at runtime.
 */

export type CmsImageFormat = "jpg" | "pjpg" | "png" | "webp";
export type CmsImageFit =
  | "clip"
  | "crop"
  | "fill"
  | "fillmax"
  | "max"
  | "scale"
  | "min";
export type CmsImageCrop =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "center"
  | "focalpoint"
  | "entropy";
export type CmsImageFlip = "h" | "v" | "hv";
export type CmsImageOrient = 0 | 90 | 180 | 270;

export interface CmsImageOptions {
  width?: number;
  height?: number;
  dpr?: number;
  quality?: number;
  format?: CmsImageFormat;
  /** Sets `auto=format` — let Sanity pick the best format for the client. */
  autoFormat?: boolean;
  fit?: CmsImageFit;
  crop?: CmsImageCrop;
  /** Gaussian blur, 0–2000. */
  blur?: number;
  /** Sharpen, 0–100. */
  sharpen?: number;
  /** Saturation, -100–100. `-100` = grayscale. */
  saturation?: number;
  flip?: CmsImageFlip;
  orient?: CmsImageOrient;
  invert?: boolean;
  /** Hex background color (3, 4, 6, or 8 hex chars, no leading `#`). */
  background?: string;
  /** Focal-point X, 0–1. Used together with `crop: "focalpoint"`. */
  focalPointX?: number;
  /** Focal-point Y, 0–1. */
  focalPointY?: number;
  rect?: { x: number; y: number; width: number; height: number };
  pad?: number;
}

const ASSET_REF_PATTERN =
  /^[a-f0-9]{40}-\d+x\d+\.(jpg|jpeg|png|webp|svg|gif|avif)$/i;
const HEX_COLOR_PATTERN = /^[a-f0-9]{3,8}$/i;

function assertValidBaseUrl(baseUrl: string): URL {
  if (!baseUrl) {
    throw new Error("cmsImageUrl: baseUrl is required");
  }
  let parsed: URL;
  try {
    parsed = new URL(baseUrl);
  } catch {
    throw new Error(`cmsImageUrl: invalid baseUrl: ${baseUrl}`);
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error(`cmsImageUrl: baseUrl must use http(s): ${baseUrl}`);
  }
  return parsed;
}

function assertValidAssetRef(assetRef: string): string {
  if (!assetRef) {
    throw new Error("cmsImageUrl: assetRef is required");
  }
  const trimmed = assetRef.replace(/^\/+/, "");
  if (!ASSET_REF_PATTERN.test(trimmed)) {
    throw new Error(
      `cmsImageUrl: assetRef must match "<hash>-WxH.ext": ${assetRef}`
    );
  }
  return trimmed;
}

function assertInt(name: string, value: number, min: number, max?: number) {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`cmsImageUrl: ${name} must be an integer (got ${value})`);
  }
  if (value < min || (max !== undefined && value > max)) {
    const range = max !== undefined ? `${min}–${max}` : `>= ${min}`;
    throw new Error(
      `cmsImageUrl: ${name} out of range ${range} (got ${value})`
    );
  }
}

function assertNumber(name: string, value: number, min: number, max: number) {
  if (!Number.isFinite(value)) {
    throw new Error(`cmsImageUrl: ${name} must be a number (got ${value})`);
  }
  if (value < min || value > max) {
    throw new Error(
      `cmsImageUrl: ${name} out of range ${min}–${max} (got ${value})`
    );
  }
}

function appendOptions(
  params: URLSearchParams,
  options: CmsImageOptions
): void {
  const {
    width,
    height,
    dpr,
    quality,
    format,
    autoFormat,
    fit,
    crop,
    blur,
    sharpen,
    saturation,
    flip,
    orient,
    invert,
    background,
    focalPointX,
    focalPointY,
    rect,
    pad
  } = options;

  if (width !== undefined) {
    assertInt("width", width, 1, 16384);
    params.set("w", String(width));
  }
  if (height !== undefined) {
    assertInt("height", height, 1, 16384);
    params.set("h", String(height));
  }
  if (dpr !== undefined) {
    assertNumber("dpr", dpr, 1, 3);
    params.set("dpr", String(dpr));
  }
  if (quality !== undefined) {
    assertInt("quality", quality, 0, 100);
    params.set("q", String(quality));
  }
  if (format !== undefined) {
    params.set("fm", format);
  }
  if (autoFormat) {
    params.set("auto", "format");
  }
  if (fit !== undefined) {
    params.set("fit", fit);
  }
  if (crop !== undefined) {
    params.set("crop", crop);
  }
  if (blur !== undefined) {
    assertInt("blur", blur, 0, 2000);
    params.set("blur", String(blur));
  }
  if (sharpen !== undefined) {
    assertInt("sharpen", sharpen, 0, 100);
    params.set("sharp", String(sharpen));
  }
  if (saturation !== undefined) {
    assertInt("saturation", saturation, -100, 100);
    params.set("sat", String(saturation));
  }
  if (flip !== undefined) {
    params.set("flip", flip);
  }
  if (orient !== undefined) {
    params.set("or", String(orient));
  }
  if (invert) {
    params.set("invert", "true");
  }
  if (background !== undefined) {
    if (!HEX_COLOR_PATTERN.test(background)) {
      throw new Error(
        `cmsImageUrl: background must be hex (3/4/6/8 chars, no "#"): ${background}`
      );
    }
    params.set("bg", background);
  }
  if (focalPointX !== undefined) {
    assertNumber("focalPointX", focalPointX, 0, 1);
    params.set("fp-x", String(focalPointX));
  }
  if (focalPointY !== undefined) {
    assertNumber("focalPointY", focalPointY, 0, 1);
    params.set("fp-y", String(focalPointY));
  }
  if (rect !== undefined) {
    assertInt("rect.x", rect.x, 0);
    assertInt("rect.y", rect.y, 0);
    assertInt("rect.width", rect.width, 1);
    assertInt("rect.height", rect.height, 1);
    params.set("rect", `${rect.x},${rect.y},${rect.width},${rect.height}`);
  }
  if (pad !== undefined) {
    assertInt("pad", pad, 0, 2000);
    params.set("pad", String(pad));
  }
}

export function buildCmsImageUrl(
  baseUrl: string,
  assetRef: string,
  options: CmsImageOptions = {}
): string {
  const base = assertValidBaseUrl(baseUrl);
  const ref = assertValidAssetRef(assetRef);

  const basePath = base.pathname.replace(/\/+$/, "");
  const url = new URL(`${basePath}/${ref}`, base);
  appendOptions(url.searchParams, options);
  return url.toString();
}

/**
 * Apply CMS image transformation params to an already-built URL.
 *
 * Use this when you have a full image URL from dynamic CMS data (e.g. a
 * block-content image) rather than a bare asset ref. Returns `""` for empty
 * input so dynamic callers can pass missing URLs without a guard.
 */
export function applyCmsImageParams(
  url: string,
  options: CmsImageOptions = {}
): string {
  if (!url) {
    return "";
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`cmsImageUrl: invalid url: ${url}`);
  }
  appendOptions(parsed.searchParams, options);
  return parsed.toString();
}

/**
 * Create an app-scoped builder that binds a baseUrl once. Each app wraps this
 * with its own `envVariables.CMS_IMAGE_PROXY_URL` so call sites only pass
 * `assetRef` + options.
 */
export function createCmsImageUrlBuilder(baseUrl: string) {
  assertValidBaseUrl(baseUrl);
  return (assetRef: string, options?: CmsImageOptions) =>
    buildCmsImageUrl(baseUrl, assetRef, options);
}
